import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { referralsAPI, vansAPI, authAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft, User, Mail, Shield, Truck, Lock, Eye, EyeOff,
  KeyRound, FileText, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

export default function TechProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assignedVehicle, setAssignedVehicle] = useState(null);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (user?.assigned_van_id) {
          const vanRes = await vansAPI.get(user.assigned_van_id);
          if (!cancelled) setAssignedVehicle(vanRes.data);
        }
      } catch { /* vehicle may not exist */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user?.assigned_van_id]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!passwordForm.currentPassword) { toast.error('Current password is required'); return; }
    if (!passwordForm.newPassword || passwordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters'); return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match'); return;
    }
    setChangingPassword(true);
    try {
      await authAPI.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password changed');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="tech-profile-loading">
        <Loader2 className="w-7 h-7 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50" data-testid="tech-profile">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200">
        <button
          onClick={() => navigate('/tech/more')}
          className="p-1.5 -ml-1 rounded-lg active:bg-slate-100"
          data-testid="tech-profile-back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-slate-700" />
        </button>
        <h1 className="text-base font-bold text-slate-900">Profile</h1>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 py-5 space-y-5">
        {/* Avatar + Name */}
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center text-white text-xl font-bold shrink-0">
            {(user?.name || 'T').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-lg font-bold text-slate-900 truncate">{user?.name}</p>
            <span className="inline-block mt-0.5 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide rounded-full bg-green-100 text-green-700">
              Technician
            </span>
          </div>
        </div>

        {/* Info rows */}
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          <InfoRow icon={Mail} label="Email" value={user?.email || '—'} testId="tech-profile-email" />
          <InfoRow icon={Shield} label="Role" value={user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : '—'} testId="tech-profile-role" />
          {(user?.assigned_van_id || assignedVehicle) && (
            <InfoRow
              icon={Truck}
              label="Assigned Vehicle"
              value={assignedVehicle ? `${assignedVehicle.name}${assignedVehicle.van_number ? ' #' + assignedVehicle.van_number : ''}` : 'Loading...'}
              testId="tech-profile-vehicle"
            />
          )}
        </div>

        {/* Password change */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="w-4 h-4 text-slate-500" />
            <h2 className="text-sm font-bold text-slate-900">Change Password</h2>
          </div>
          <form onSubmit={handlePasswordChange} className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">Current Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                  className="h-10 text-sm pr-10 bg-slate-50 border-slate-200"
                  data-testid="tech-profile-current-pw"
                />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 p-1" onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                  {showCurrentPassword ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">New Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  placeholder="Min. 6 characters"
                  className="h-10 text-sm pr-10 bg-slate-50 border-slate-200"
                  data-testid="tech-profile-new-pw"
                />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 p-1" onClick={() => setShowNewPassword(!showNewPassword)}>
                  {showNewPassword ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
                className="h-10 text-sm mt-1 bg-slate-50 border-slate-200"
                data-testid="tech-profile-confirm-pw"
              />
            </div>
            <Button
              type="submit"
              disabled={changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
              className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-sm font-bold"
              data-testid="tech-profile-change-pw-btn"
            >
              {changingPassword && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Change Password
            </Button>
          </form>
        </div>

        {/* Legal */}
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          <a
            href="https://stockandload.com/terms-of-service/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-3 active:bg-slate-50"
            data-testid="tech-profile-terms"
          >
            <FileText className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-700">Terms of Service</span>
          </a>
          <a
            href="https://stockandload.com/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-3 active:bg-slate-50"
            data-testid="tech-profile-privacy"
          >
            <Shield className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-700">Privacy Policy</span>
          </a>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3" data-testid={testId}>
      <Icon className="w-4 h-4 text-slate-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] text-slate-400 uppercase tracking-wide font-medium">{label}</p>
        <p className="text-sm font-semibold text-slate-800 truncate">{value}</p>
      </div>
    </div>
  );
}
